import { FC, ReactNode, useCallback, useEffect, useState } from 'react';
import { AiOutlineCar } from 'react-icons/ai';
import { FaCarSide } from 'react-icons/fa';
import { FiSearch } from 'react-icons/fi';
import { MdKeyboardArrowRight, MdKeyboardArrowUp } from 'react-icons/md';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { AdminTitle } from '../../../components/AdminTitle';
import { LoadingConsult } from '../../../components/LoadingConsult';
import { StyledButton } from '../../../components/StyledButton';
import { StatusModalConfirmClick } from '../../../components/StyledModalConfirmClick';
import StyledSectionHideButton from '../../../components/StyledSectionHideButton';
import {
  listConsultsByParameters,
  useConsultsBySub,
} from '../../../hooks/useConsults';
import { consultDebtsToUpdateInfo } from '../../../hooks/useDebtsUserVehicle';
import theme from '../../../styles/themes/default';
import { checkToFormatLicensingStatus } from '../../../utils/checkToFormatLicensingStatus';
import {
  ISetDataVehicleInfoToShow,
  setDataVehicleInfoToShow,
} from '../../../utils/setDataVehicleInfoToShow';
import { ufsNamed } from '../../../utils/ufsNamed';
import {
  ContentWrapper,
  DebtsContainer,
  OrdersContainer,
  TitleToConsultWrapper,
  UserVehicleDetailsContainer,
  VehicleInfoContainer,
} from './styles';
import { IConsult, usePayment } from '../../../context/Payments';
import { findConsultById, updateConsult } from '../../../hooks/usePayments';
import { useUser } from '../../../context/UserContext';

import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';

const StyledTooltipContainer = styled(
  ({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ),
)(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    fontSize: 16,
  },
}));

const UserVehicleDetails: FC = () => {
  const { user, updateUser } = useUser();
  const navigate = useNavigate();
  let [searchParams] = useSearchParams();

  const { getDebts, handleSetConsults } = usePayment();

  const userVehicleId = searchParams.get('id');
  const driver = searchParams.get('driver');
  const uf = searchParams.get('uf');

  const userVehicleInfoStorage = localStorage.getItem('userVehicleInfo');

  const [fullInfo, setFullInfo] = useState<any | undefined>(undefined);

  const [showConsultsDebtsType, setShowConsultsDebtsType] = useState<any>([]);
  const [showMainConsult, setShowMainConsult] = useState<any>({});

  const [arrayVehicleInfoToShow, setArrayVehicleInfoToShow] = useState<
    ISetDataVehicleInfoToShow | undefined
  >(undefined);

  const [loading, setLoading] = useState(false);
  const [fast, setFast] = useState(false);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [typeConfirmModal, setTypeConfirmModal] = useState<
    'success' | 'error' | 'warning' | 'info' | 'question'
  >('question');
  const [messageConfirmModal, setMessageConfirmModal] = useState<
    string | ReactNode
  >('');
  const [arrayActionsConfirmModal, setArrayActionsConfirmModal] = useState<
    Array<() => void>
  >([]);

  const { data, isLoading, refetch } = useConsultsBySub({
    resultsPerPage: 10,
    page: 1,
    orderBy: 'updated_at',
    orderByDirection: 'asc',
    placa: fullInfo?.vehicle?.placa,
  });

  useEffect(() => {
    if (fullInfo?.consults?.length > 0) {
      const states = fullInfo?.consults?.reduce(
        (acc: any, c: any) => ({ ...acc, [c.codigo]: false }),
        {},
      );
      setShowMainConsult(states);
    }
  }, [fullInfo?.consults]);

  const handleListDebtsConsults = useCallback(async () => {
    try {
      if (userVehicleInfoStorage) {
        setFast(false);
        setLoading(true);
        const vehicleInfoLocalStorageParsed = JSON.parse(
          userVehicleInfoStorage,
        );

        const arrayToShow = setDataVehicleInfoToShow(
          vehicleInfoLocalStorageParsed,
          driver,
        );
        setArrayVehicleInfoToShow(arrayToShow);

        if (vehicleInfoLocalStorageParsed?.consult?.correlation) {
          const consultsList = await listConsultsByParameters({
            correlation: vehicleInfoLocalStorageParsed?.consult?.correlation,
          });

          const listDataConsults = consultsList?.map(
            (each) => each?.dataConsulta,
          );
          console.log('🚀 ~ listDataConsults:', listDataConsults);

          handleSetConsults((listDataConsults as IConsult[]) || []);

          if (!fullInfo) {
            setFullInfo({
              ...vehicleInfoLocalStorageParsed,
              consults: consultsList?.map((each) => each?.dataConsulta),
            });

            const states = consultsList?.map(() => {
              return {
                open: false,
                ipva: false,
                licenciamento: false,
                dpvat: false,
                outros: false,
                multas: false,
              };
            });
            if (states) {
              setShowConsultsDebtsType([...states]);
            }
          }
        } else {
          setFullInfo({
            ...vehicleInfoLocalStorageParsed,
          });
        }
        setFast(true);
        setLoading(false);
      }
    } catch (e: any) {
      console.log('🚀 ~ e:', { ...e });
    } finally {
      setFast(true);
      setLoading(false);
    }
  }, [driver, fullInfo, handleSetConsults, userVehicleInfoStorage]);

  useEffect(() => {
    if (userVehicleInfoStorage) {
      handleListDebtsConsults();
    }
  }, [userVehicleInfoStorage]);

  const loadDebts = useCallback(async (vehicleId: number) => {
    setLoading(true);
    setFast(false);
    try {
      const fullInfoData = await consultDebtsToUpdateInfo(vehicleId);

      if (fullInfoData.message === 'success') {
        setFullInfo((prev: any) => ({
          ...prev,
          ...fullInfoData,
          ...fullInfoData?.userVehicleUpdated,
          consult: fullInfoData?.consults?.find(
            (consult: any) =>
              consult?.codigo === fullInfoData?.userVehicleUpdated?.lastConsult,
          ),
        }));

        const states = fullInfoData?.consults.map(() => {
          return {
            open: false,
            ipva: false,
            licenciamento: false,
            dpvat: false,
            outros: false,
            multas: false,
          };
        });

        setShowConsultsDebtsType([...states]);

        const arrayVehicleInfoToShow = setDataVehicleInfoToShow(
          fullInfoData,
          driver,
        );
        setArrayVehicleInfoToShow(arrayVehicleInfoToShow);

        Swal.fire({
          icon: 'success',
          title: 'Sucesso!',
          text: 'Consulta efetuada com sucesso. Veja nos detalhes!',
          confirmButtonColor: theme.colors.orange.primary,
        }).then(() => {
          updateUser();
          refetch();
          setFast(true);
          setLoading(false);
          return;
        });
      } else {
        console.log(
          '🚀 ~ Check exception: index.tsx:114 ~ loadDebts ~ fullInfoData:',
          fullInfoData,
        );
      }
    } catch (e: any) {
      console.log('🚀 ~ file: index.tsx:174 ~ loadDebts ~ e:', e);
      if (e?.response?.status === 404) {
        setFast(true);
        const fullInfoData = e?.response?.data;
        if (
          fullInfoData?.message?.includes('Nenhum débito encontrado') ||
          fullInfoData?.message?.includes('Boa notícia') ||
          fullInfoData?.userVehicleUpdated?.warning
        ) {
          setFullInfo((prev: any) => ({
            ...prev,
            ...fullInfoData,
            ...fullInfoData?.userVehicleUpdated,
          }));

          const arrayVehicleInfoToShow = setDataVehicleInfoToShow(
            fullInfoData,
            driver,
          );
          setArrayVehicleInfoToShow(arrayVehicleInfoToShow);

          Swal.fire({
            icon: 'info',
            title: fullInfoData?.userVehicleUpdated?.warning
              ? 'Veículo com dívida ativa'
              : 'Consulta efetuada com sucesso!',
            html:
              fullInfoData?.userVehicleUpdated?.warning ||
              fullInfoData?.message,
            confirmButtonColor: theme.colors.orange.primary,
          }).then(() => {
            updateUser();
            refetch();
            setLoading(false);
            return;
          });
        } else if (
          fullInfoData?.message?.includes('Erro consultando débitos')
        ) {
          Swal.fire({
            icon: 'info',
            title: 'Não foi possível consultar débitos',
            text:
              'Tente novamente ou fale com o suporte - Erro: ' +
              fullInfoData?.message,
            confirmButtonColor: theme.colors.orange.primary,
          }).then(() => {
            updateUser();
            setFast(true);
            setLoading(false);
            return;
          });
          return;
        } else {
          Swal.fire({
            icon: 'info',
            title: 'Houve um problema na consulta',
            text: 'Favor tentar novamente mais tarde, ou entrar em contato com o suporte.',
            confirmButtonColor: theme.colors.orange.primary,
          }).then(() => {
            updateUser();
            setFast(true);
            setLoading(false);
            return;
          });
        }
      } else if (
        e?.response?.status === 403 &&
        e?.response?.data?.message?.includes('Limite de consultas atingido')
      ) {
        Swal.fire({
          icon: 'error',
          title: 'Erro!',
          html: e?.response?.data?.message ?? `Limite de consultas atingido!`,
          confirmButtonColor: theme.colors.orange.primary,
        }).then(() => {
          updateUser();
          setFast(true);
          setLoading(false);
          navigate('/vehicles');
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Erro!',
          html: `Não foi possível consultar débitos do veículo. Tente novamente mais tarde ou entre em contato com o suporte.`,
          confirmButtonColor: theme.colors.orange.primary,
        }).then(() => {
          updateUser();
          setFast(true);
          setLoading(false);
          return;
        });
      }
      setFast(true);
      setLoading(false);
    }
  }, []);

  const handleSelectPayment = useCallback(
    async (consult: IConsult) => {
      // setLoading(true);
      // // const token = await recaptchaRef.current.executeAsync();

      // // if (!token) {
      // //   setLoading(false);
      // //   return;
      // // }

      // getDebts(consult)
      //   .then(() => {
      //     console.log('teste');
      //   })
      //   .catch((error) => {
      //     console.log({ e: error });
      //   });

      // await findConsultById(consult.codigo).then(async (storageConsult) => {
      //   if (storageConsult !== 'There is no such consult') {
      //     const data = {
      //       ...storageConsult,
      //       subUsuario: user?.sub || null,
      //       userPool: user?.sub ? 'Cadastro' : 'Pre-Debitos',
      //       dataConsulta: {
      //         ...storageConsult?.dataConsulta,
      //         nome: user?.name,
      //       },
      //       payloadConsulta: consult,
      //       subStatus: 'selection',
      //     };

      //     await updateConsult(data);
      //   }
      // });

      navigate(`/debts`);
    },
    [getDebts, user],
  );

  const consultModalMessage = useCallback((): ReactNode => {
    if (uf === 'SE') {
      return (
        <div>
          <p>
            Para parcelar os débitos do seu veículo, você será direcionado para
            o site do
            <a
              href="https://www.detran.se.gov.br/portal/?pg=cons_veiculo"
              target="_blank"
              rel="noreferrer"
            >
              {' '}
              Detran SE
            </a>
            , onde poderá preencher os dados solicitados e consultar os débitos
            existentes. Ao final da consulta, basta escolher a Zignet para
            efetuar o parcelamento.
          </p>
        </div>
      );
    }

    return (
      <div>
        <p>Deseja realmente fazer uma nova consulta?</p>
        {user && Object.keys(user).length > 0 && (
          <p>
            Número de consultas:{' '}
            {user?.initial_consult_balance - user?.consult_balance}
          </p>
        )}
      </div>
    );
  }, [uf, user?.initial_consult_balance, user?.consult_balance]);

  const consultModalAction = useCallback(
    (userVehicleId: number): (() => Promise<any>)[] => {
      if (uf === 'SE') {
        return [
          async () =>
            window.open(
              'https://www.detran.se.gov.br/portal/?pg=cons_veiculo',
              '_blank',
            ),
        ];
      }
      return [async () => await loadDebts(userVehicleId)];
    },
    [uf],
  );

  const handleToLoadDebts = useCallback(async (userVehicleId: number) => {
    setTypeConfirmModal('question');
    setMessageConfirmModal(consultModalMessage());
    setArrayActionsConfirmModal(consultModalAction(userVehicleId));
    setShowConfirmModal(true);
  }, []);

  const handleToCountDebts = (debts: any) =>
    debts.ipva.length +
    debts.dpvat.length +
    debts.licenciamento.length +
    debts.multas.length +
    debts.outros.length;

  const handleToSpreadDebts = (debts: any) => [
    ...debts.ipva,
    ...debts.dpvat,
    ...debts.licenciamento,
    ...debts.multas,
    ...debts.outros,
  ];

  const calculateTotalForDebts = (consult: IConsult): number => {
    const allDebts = [
      ...(consult?.debitos?.licenciamento || []),
      ...(consult?.debitos?.ipva || []),
      ...(consult?.debitos?.multas || []),
      ...(consult?.debitos?.dpvat || []),
      ...(consult?.debitos?.outros || []),
    ];

    return allDebts.reduce((acc, cur) => acc + cur?.valor || 0, 0);
  };

  const handleConsultName = (name: string) => {
    return name.replace('_', ' ').toUpperCase();
  };

  const handleConsultCode = (info: any) => {
    let txt = '';

    if (info?.consults?.length < 2) {
      txt = `${info?.lastConsult ?? info?.lastConsultAtDynamo}`;
      return txt;
    }

    if (info?.consults?.length < 4) {
      txt = '';
      info.consults.forEach((consult: any, i: number) => {
        txt += consult?.codigo?.toString();
        if (i + 1 < info.consults.length) {
          txt += ', ';
        }
      });
    }

    if (info?.consults?.length >= 4) {
      txt = `${info.consults[0].codigo}, ${info.consults[1].codigo}, ${info.consults[2].codigo}`;

      txt += ` e outras ${info.consults.length - 3}`;

      let toolTipText = '';

      info.consults.slice(3).forEach((consult: any, i: number) => {
        toolTipText += consult?.codigo?.toString();
        if (i + 1 < info.consults.slice(3).length) {
          toolTipText += ', ';
        }
      });

      return (
        <StyledTooltipContainer title={`${toolTipText}`} enterDelay={100} arrow>
          <Typography variant="inherit">{`(${txt})`}</Typography>
        </StyledTooltipContainer>
      );
    }

    return `(${txt})`;
  };

  return (
    <UserVehicleDetailsContainer>
      <AdminTitle title="Detalhe do Veículo" icon={<AiOutlineCar />} />
      {(loading || isLoading) && (
        <LoadingConsult
          fast={fast}
          setLoading={setLoading}
          texts={[
            'Requisição iniciada',
            'Estamos buscando sua placa. Por favor, aguarde',
            'Acessando os dados do veículo no Detran',
            'Processamento de débitos em andamento',
            'Sua consulta está sendo finalizada',
          ]}
          time={120}
        />
      )}
      {showConfirmModal && (
        <StatusModalConfirmClick
          type={typeConfirmModal}
          text={messageConfirmModal}
          setModal={setShowConfirmModal}
          callActions={arrayActionsConfirmModal}
          twoButtons={true}
        />
      )}
      <ContentWrapper>
        <TitleToConsultWrapper>
          <aside>
            <span>
              <h2>Data da última consulta realizada: </h2>
              <p>
                {fullInfo?.lastConsult || fullInfo?.lastConsultAtDynamo ? (
                  <p style={{ display: 'flex', gap: '4px' }}>
                    {`${new Date(
                      fullInfo?.consults?.find(
                        (consult: any) =>
                          consult?.codigo ===
                          (fullInfo?.lastConsult ??
                            fullInfo?.lastConsultAtDynamo),
                      )?.horario,
                    )?.toLocaleDateString()} - `}{' '}
                    {handleConsultCode(fullInfo)}
                  </p>
                ) : fullInfo?.warning ? (
                  `AVISO: ${fullInfo?.warning}`
                ) : (
                  `Veículo sem Consultas ou não foram encontrado débitos na ultima atualização`
                )}
              </p>
            </span>
            <hr style={{ margin: '8px' }} />
            {user && Object.keys(user).length > 0 && (
              <p>
                Quantidade de consultas já realizadas:{' '}
                <strong>
                  {user?.initial_consult_balance - user?.consult_balance}
                </strong>
              </p>
            )}
          </aside>
          <StyledButton
            backgroundColor={theme.colors.orange.primary}
            text={'Consultar Débitos'}
            type="button"
            onClick={() => handleToLoadDebts(Number(userVehicleId))}
          />
        </TitleToConsultWrapper>
        <StyledSectionHideButton
          titleSection={'INFORMAÇÕES DO VEÍCULO'}
          contentSectionContainer={
            <VehicleInfoContainer>
              {arrayVehicleInfoToShow?.map((item: any, index: number) => (
                <div key={index + item}>
                  <p
                    style={{
                      fontWeight:
                        Object.keys(item)[0] === 'Status' ||
                        Object.keys(item)[0] === 'Condutor'
                          ? 700
                          : 600,
                      color:
                        Object.keys(item)[0] === 'Status' ||
                        Object.keys(item)[0] === 'Condutor'
                          ? theme.colors.gray[600]
                          : theme.colors.gray[500],
                    }}
                  >
                    {' '}
                    {Object.keys(item)}
                  </p>
                  <p
                    style={{
                      color:
                        Object.keys(item)[0] === 'Status' &&
                        userVehicleInfoStorage
                          ? checkToFormatLicensingStatus(
                              JSON.parse(userVehicleInfoStorage)?.vehicle,
                            ).color
                          : theme.colors.gray[500],
                      fontWeight: Object.keys(item)[0] === 'Status' ? 700 : 600,
                    }}
                  >
                    {Object.values(item)}
                  </p>
                </div>
              ))}
              {fullInfo?.warning?.length > 0 && (
                <p style={{ color: theme.colors.orange.primary }}>
                  <strong style={{ color: theme.colors.gray[900] }}>
                    Aviso:{' '}
                  </strong>
                  {fullInfo?.warning}
                </p>
              )}
            </VehicleInfoContainer>
          }
        />
        <StyledSectionHideButton
          titleSection={'DÉBITOS'}
          contentSectionContainer={
            <DebtsContainer>
              <header>
                <div>
                  <span>
                    <FaCarSide /> {fullInfo?.vehicle?.placa}
                  </span>
                  <span>
                    <FiSearch /> DETRAN{' - '}
                    {ufsNamed
                      .find((each) => each.id === fullInfo?.vehicle?.uf)
                      ?.id.toUpperCase()}
                  </span>
                </div>
                {!(
                  fullInfo?.consults?.length === 0 ||
                  (!fullInfo?.lastConsult && !fullInfo?.lastConsultAtDynamo)
                ) && (
                  <p>
                    Total Geral:{' '}
                    {`R$ ${Number(fullInfo?.fullTotalDebts ?? 0).toLocaleString(
                      'pt-br',
                      {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 2,
                      },
                    )}`}
                  </p>
                )}
              </header>

              {fullInfo?.consults?.length === 0 ||
              (!fullInfo?.lastConsult && !fullInfo?.lastConsultAtDynamo) ? (
                <p>Sem Débitos</p>
              ) : (
                <div className="totalValueLine">
                  <h1>TIPO DE CONSULTA:</h1>
                </div>
              )}

              {fullInfo?.consults?.length > 0 &&
                fullInfo?.consults?.map((consult: any, index: number) => {
                  let hasDebts = false;

                  const debts = consult.debitos;
                  for (const key in debts) {
                    if (debts.hasOwnProperty(key)) {
                      if (debts[key].length > 0) hasDebts = true;
                    }
                  }

                  if (hasDebts) {
                    return (
                      consult?.sucesso && (
                        <>
                          {index > 0 && index < fullInfo?.consults?.length && (
                            <hr style={{ margin: '8px' }} />
                          )}
                          <section key={consult?.codigo}>
                            <aside>
                              <span
                                onClick={() => {
                                  setShowMainConsult((prev: any) => ({
                                    ...prev,
                                    [String(consult.codigo)]:
                                      !showMainConsult[consult.codigo],
                                  }));
                                }}
                                style={{ color: theme.colors.gray[900] }}
                              >
                                {`${handleConsultName(
                                  consult?.nome_consulta,
                                )} (${handleToCountDebts(
                                  consult?.debitos,
                                )}) `}{' '}
                                {showMainConsult[consult.codigo] ? (
                                  <MdKeyboardArrowUp />
                                ) : (
                                  <MdKeyboardArrowRight />
                                )}
                              </span>
                              <span>
                                {`Total: R$ ${(
                                  calculateTotalForDebts(consult) || 0
                                ).toLocaleString('pt-br', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}`}
                              </span>
                            </aside>

                            {showMainConsult[consult.codigo] &&
                              consult?.debitos?.ipva?.length > 0 && (
                                <div>
                                  <aside>
                                    <span
                                      onClick={() => {
                                        let states = showConsultsDebtsType;
                                        states[index].ipva =
                                          !states[index].ipva;
                                        setShowConsultsDebtsType([...states]);
                                      }}
                                    >
                                      {`IPVA (${
                                        fullInfo?.consults?.length > 0
                                          ? consult?.debitos?.ipva?.length
                                          : '0'
                                      }) `}{' '}
                                      {showConsultsDebtsType?.[index].ipva ? (
                                        <MdKeyboardArrowUp />
                                      ) : (
                                        <MdKeyboardArrowRight />
                                      )}
                                    </span>
                                    {showConsultsDebtsType?.[index].ipva && (
                                      <p>
                                        {`Total: R$ ${consult?.debitos?.ipva
                                          ?.reduce((acc: number, each: any) => {
                                            const hasUniqueQuota =
                                              consult?.debitos?.ipva?.some(
                                                (debt: any) =>
                                                  !debt.descricaoAdicional.includes(
                                                    'parcela',
                                                  ),
                                              );

                                            if (hasUniqueQuota) {
                                              return !each?.descricaoAdicional.includes(
                                                'parcela',
                                              )
                                                ? acc + each.valor
                                                : acc;
                                            }
                                            return acc + each.valor;
                                          }, 0.0)
                                          .toLocaleString('pt-br', {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                          })}`}
                                      </p>
                                    )}
                                  </aside>

                                  {showConsultsDebtsType?.[index].ipva && (
                                    <article>
                                      {consult?.debitos?.ipva?.map(
                                        (each: any) => (
                                          <span>
                                            {each.descricao +
                                              ' ' +
                                              each.descricaoAdicional +
                                              ' - R$ ' +
                                              each.valor?.toLocaleString(
                                                'pt-br',
                                                {
                                                  minimumFractionDigits: 2,
                                                  maximumFractionDigits: 2,
                                                },
                                              )}
                                          </span>
                                        ),
                                      )}
                                    </article>
                                  )}
                                </div>
                              )}

                            {showMainConsult[consult.codigo] &&
                              consult?.debitos?.dpvat?.length > 0 && (
                                <>
                                  <aside>
                                    <span
                                      onClick={() => {
                                        let states = showConsultsDebtsType;
                                        states[index].dpvat =
                                          !states[index].dpvat;
                                        setShowConsultsDebtsType([...states]);
                                      }}
                                    >
                                      {`DPVAT (${consult?.debitos?.dpvat?.length}) `}{' '}
                                      {showConsultsDebtsType?.[index].dpvat ? (
                                        <MdKeyboardArrowUp />
                                      ) : (
                                        <MdKeyboardArrowRight />
                                      )}
                                    </span>
                                    {showConsultsDebtsType?.[index].dpvat && (
                                      <p>
                                        {`Total: R$ ${consult?.debitos?.dpvat
                                          ?.reduce(
                                            (acc: number, each: any) =>
                                              acc + each.valor,
                                            0.0,
                                          )
                                          .toLocaleString('pt-br', {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                          })}`}
                                      </p>
                                    )}
                                  </aside>
                                  {showConsultsDebtsType?.[index].dpvat && (
                                    <article>
                                      {consult?.debitos?.dpvat?.map(
                                        (each: any) => (
                                          <span>
                                            {each.descricao +
                                              ' ' +
                                              each.descricaoAdicional +
                                              ' - R$ ' +
                                              each.valor?.toLocaleString(
                                                'pt-br',
                                                {
                                                  minimumFractionDigits: 2,
                                                  maximumFractionDigits: 2,
                                                },
                                              )}
                                          </span>
                                        ),
                                      )}
                                    </article>
                                  )}
                                </>
                              )}

                            {showMainConsult[consult.codigo] &&
                              consult?.debitos?.licenciamento?.length > 0 && (
                                <div>
                                  <aside>
                                    <span
                                      onClick={() => {
                                        let states = showConsultsDebtsType;
                                        states[index].licenciamento =
                                          !states[index].licenciamento;
                                        setShowConsultsDebtsType([...states]);
                                      }}
                                    >
                                      {`LICENCIAMENTO (${consult?.debitos?.licenciamento?.length}) `}{' '}
                                      {showConsultsDebtsType?.[index]
                                        .licenciamento ? (
                                        <MdKeyboardArrowUp />
                                      ) : (
                                        <MdKeyboardArrowRight />
                                      )}
                                    </span>
                                    {showConsultsDebtsType?.[index]
                                      .licenciamento && (
                                      <p>
                                        {`Total: R$ ${consult?.debitos?.licenciamento
                                          ?.reduce(
                                            (acc: number, each: any) =>
                                              acc + each.valor,
                                            0.0,
                                          )
                                          .toLocaleString('pt-br', {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                          })}`}
                                      </p>
                                    )}
                                  </aside>
                                  {showConsultsDebtsType?.[index]
                                    .licenciamento && (
                                    <article>
                                      {consult?.debitos?.licenciamento?.map(
                                        (each: any) => (
                                          <span>
                                            {each.descricao +
                                              ' ' +
                                              each.descricaoAdicional +
                                              ' - R$ ' +
                                              each.valor?.toLocaleString(
                                                'pt-br',
                                                {
                                                  minimumFractionDigits: 2,
                                                  maximumFractionDigits: 2,
                                                },
                                              )}
                                          </span>
                                        ),
                                      )}
                                    </article>
                                  )}
                                </div>
                              )}

                            {showMainConsult[consult.codigo] &&
                              consult?.debitos?.multas?.length > 0 && (
                                <div>
                                  <aside>
                                    <span
                                      onClick={() => {
                                        let states = showConsultsDebtsType;
                                        states[index].multas =
                                          !states[index].multas;
                                        setShowConsultsDebtsType([...states]);
                                      }}
                                    >
                                      {`MULTAS (${consult?.debitos?.multas?.length}) `}{' '}
                                      {showConsultsDebtsType?.[index].multas ? (
                                        <MdKeyboardArrowUp />
                                      ) : (
                                        <MdKeyboardArrowRight />
                                      )}
                                    </span>
                                    {showConsultsDebtsType?.[index].multas && (
                                      <p>
                                        {`Total: R$ ${consult?.debitos?.multas
                                          ?.reduce(
                                            (acc: number, each: any) =>
                                              acc + each.valor,
                                            0.0,
                                          )
                                          .toLocaleString('pt-br', {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                          })}`}
                                      </p>
                                    )}
                                  </aside>

                                  {showConsultsDebtsType?.[index].multas && (
                                    <article
                                      style={{
                                        maxHeight: '380px',
                                        overflow: 'auto',
                                      }}
                                    >
                                      {consult?.debitos?.multas?.map(
                                        (each: any, index: number) => (
                                          <section
                                            className="multasDescription"
                                            key={index}
                                            style={{
                                              padding: '15px 0',
                                              borderBottom:
                                                consult?.debitos?.multas
                                                  .length -
                                                  1 !==
                                                index
                                                  ? '1px solid #EAE7E7'
                                                  : '',
                                            }}
                                          >
                                            <span className="description">
                                              <p>Descrição</p>
                                              {each.descricao}
                                            </span>
                                            <span>
                                              <p style={{ fontWeight: 700 }}>
                                                Descrição Adicional
                                              </p>
                                              {each.descricaoAdicional}
                                            </span>
                                            <span>
                                              <p
                                                style={{
                                                  fontWeight: 700,
                                                }}
                                              >
                                                Valor
                                              </p>
                                              R${' '}
                                              {each.valor?.toLocaleString(
                                                'pt-br',
                                                {
                                                  minimumFractionDigits: 2,
                                                  maximumFractionDigits: 2,
                                                },
                                              )}
                                            </span>
                                          </section>
                                        ),
                                      )}
                                    </article>
                                  )}
                                </div>
                              )}

                            {showMainConsult[consult.codigo] &&
                              consult?.debitos?.outros?.length > 0 && (
                                <div>
                                  <aside>
                                    <span
                                      onClick={() => {
                                        let states = showConsultsDebtsType;
                                        states[index].outros =
                                          !states[index]?.outros;
                                        setShowConsultsDebtsType([...states]);
                                      }}
                                    >
                                      {`OUTROS (${consult?.debitos?.outros?.length}) `}{' '}
                                      {showConsultsDebtsType?.[index]
                                        ?.outros ? (
                                        <MdKeyboardArrowUp />
                                      ) : (
                                        <MdKeyboardArrowRight />
                                      )}
                                    </span>
                                    {showConsultsDebtsType?.[index]?.outros && (
                                      <p>
                                        {`Total: R$ ${consult?.debitos?.outros
                                          ?.reduce(
                                            (acc: number, each: any) =>
                                              acc + each.valor,
                                            0.0,
                                          )
                                          .toLocaleString('pt-br', {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                          })}`}
                                      </p>
                                    )}
                                  </aside>

                                  {showConsultsDebtsType?.[index]?.outros && (
                                    <article>
                                      {fullInfo?.consults?.length > 0 &&
                                        consult?.debitos?.outros?.map(
                                          (each: any) => (
                                            <span>
                                              {each.descricao +
                                                ' ' +
                                                each.descricaoAdicional +
                                                ' - R$ ' +
                                                each.valor?.toLocaleString(
                                                  'pt-br',
                                                  {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                  },
                                                )}
                                            </span>
                                          ),
                                        )}
                                    </article>
                                  )}
                                </div>
                              )}

                            {showMainConsult[consult.codigo] && (
                              <div className="payButton">
                                <StyledButton
                                  backgroundColor={theme.colors.blue.primary}
                                  text={'Pagar consulta'}
                                  type="button"
                                  onClick={() => handleSelectPayment(consult)}
                                />
                              </div>
                            )}
                          </section>
                        </>
                      )
                    );
                  }
                })}
            </DebtsContainer>
          }
        />
        <StyledSectionHideButton
          titleSection={'PEDIDOS REALIZADOS'}
          contentSectionContainer={
            <OrdersContainer>
              {data && data?.results?.length > 0 ? (
                data.results.map((each: any) => {
                  if (each?.payloadCaptura) {
                    return (
                      <div key={each.codigo}>
                        <span>
                          <h3>Pedido</h3>
                          <h4>#{each?.consulta}</h4>
                        </span>
                        <span>
                          <h3>Data</h3>
                          {new Date(each?.created_at).toLocaleDateString()}
                        </span>

                        <span>
                          <h3>Placa do Carro</h3>
                          {each?.placa}
                        </span>

                        <span>
                          <h3>Valor</h3>
                          {each?.payloadCaptura?.cardNumber ||
                          each?.payloadCaptura?.cartoes?.length
                            ? Number(
                                each?.payloadCaptura?.installment?.totalPrice ||
                                  each?.payloadCaptura?.valor,
                              ).toLocaleString('pt-BR', {
                                style: 'currency',
                                currency: 'BRL',
                                minimumFractionDigits: 2,
                              })
                            : each?.payloadCaptura?.pix?.totalPrice
                            ? Number(
                                each?.payloadCaptura?.pix?.totalPrice,
                              ).toLocaleString('pt-BR', {
                                style: 'currency',
                                currency: 'BRL',
                                minimumFractionDigits: 2,
                              })
                            : 0}
                        </span>

                        <span>
                          <h3>Status</h3>
                          {each?.status}
                        </span>

                        <span>
                          <h3>Forma de Pagamento</h3>
                          {each?.payloadCaptura?.cardNumber ||
                          each?.payloadCaptura?.cartoes?.length ? (
                            <span>
                              {`Cartão de Crédito (${
                                each?.payloadCaptura?.installment?.month ||
                                each?.payloadCaptura?.parcelas
                              }x)`}
                            </span>
                          ) : (
                            <h5>Pix</h5>
                          )}
                        </span>
                      </div>
                    );
                  }
                  return null;
                })
              ) : (
                <p>Este veículo não contém pedidos realizados</p>
              )}
            </OrdersContainer>
          }
        />
      </ContentWrapper>
    </UserVehicleDetailsContainer>
  );
};

export default UserVehicleDetails;
